import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    const isAuthenticated = this.authService.isAuthenticated();

    if (isAuthenticated) {
      // Check if token needs refresh
      this.authService.checkAndRefreshToken();
      return true;
    }

    // Not authenticated, redirect to login
    this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl: state.url }
    });
    return false;
  }
}

/**
 * Functional form of AuthGuard (recommended for newer Angular versions)
 * Includes token expiry check in addition to AuthService.isAuthenticated()
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Check token expiry directly from localStorage as a fast-path
  const token = localStorage.getItem('hub_access_token');
  const expiresIn = localStorage.getItem('hub_token_expires_in');

  if (!token) {
    router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  // If expiry is stored, enforce it
  if (expiresIn && Date.now() > Number.parseInt(expiresIn, 10)) {
    localStorage.clear();
    router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  const isAuthenticated = authService.isAuthenticated();

  if (isAuthenticated) {
    authService.checkAndRefreshToken();
    return true;
  }

  // Not authenticated, redirect to login
  router.navigate(['/auth/login'], {
    queryParams: { returnUrl: state.url }
  });
  return false;
};
